import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiShoppingCart, FiMinus, FiPlus, FiStar, FiTruck, FiShield, FiPackage, FiTag } from 'react-icons/fi';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import ProductCard from '../components/ui/ProductCard';
import { formatPrice, getImageUrl } from '../utils/helpers';
import Spinner from '../components/ui/Spinner';
import API from '../api/axios';
import toast from 'react-hot-toast';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [mainImage, setMainImage] = useState(0);
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    API.get(`/products/${id}`)
      .then(({ data }) => {
        setProduct(data);
        setMainImage(0);
        if (data.category?._id) {
          API.get(`/products?category=${data.category._id}&limit=4`).then(({ data: r }) =>
            setRelated(r.products.filter((p) => p._id !== data._id).slice(0, 4))
          ).catch(() => {});
        }
      })
      .catch(() => toast.error('Product not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAddToCart = () => {
    addToCart(product, qty);
    toast.success(`${product.name} added to cart!`);
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error('Please login to leave a review');
    setSubmittingReview(true);
    try {
      await API.post(`/products/${product._id}/reviews`, review);
      toast.success('Review submitted!');
      const { data } = await API.get(`/products/${id}`);
      setProduct(data);
      setReview({ rating: 5, comment: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error submitting review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <div className="container-custom py-16"><Spinner /></div>;
  if (!product) return <div className="container-custom py-16 text-center text-gray-500">Product not found</div>;

  const discount = product.comparePrice ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100) : 0;

  return (
    <div className="container-custom py-8">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-primary-600">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/shop" className="hover:text-primary-600">Shop</Link>
        {product.category && (
          <><span className="mx-2">/</span>
          <Link to={`/shop?category=${product.category._id}`} className="hover:text-primary-600">{product.category.name}</Link></>
        )}
        <span className="mx-2">/</span>
        <span className="text-gray-800 font-medium">{product.name}</span>
      </nav>

      {/* Product details */}
      <div className="card p-6 md:p-8 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <div className="bg-gray-50 rounded-xl overflow-hidden mb-3" style={{ paddingBottom: '70%', position: 'relative' }}>
              <img
                src={getImageUrl(product.images?.[mainImage])}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-contain p-6"
                onError={(e) => { e.target.src = '/placeholder.png'; }}
              />
              {(product.onSale || discount > 0) && (
                <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-lg shadow">
                  {discount > 0 ? `-${discount}% OFF` : 'SALE'}
                </span>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setMainImage(i)} className={`w-16 h-16 flex-shrink-0 rounded-lg border-2 overflow-hidden bg-gray-50 transition-colors ${i === mainImage ? 'border-primary-500' : 'border-gray-200 hover:border-gray-300'}`}>
                    <img src={getImageUrl(img)} alt="" className="w-full h-full object-contain p-1" onError={(e) => { e.target.src = '/placeholder.png'; }} />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {product.category && (
                <Link to={`/shop?category=${product.category._id}`} className="text-xs font-semibold text-primary-600 bg-primary-50 px-3 py-1 rounded-full hover:bg-primary-100 transition-colors">
                  {product.category.name}
                </Link>
              )}
              {product.onSale && (
                <span className="flex items-center gap-1 text-xs font-bold text-white bg-red-500 px-3 py-1 rounded-full">
                  <FiTag size={10} /> SALE
                </span>
              )}
              {product.isFeatured && (
                <span className="text-xs font-bold text-amber-700 bg-amber-100 px-3 py-1 rounded-full">Featured</span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 leading-tight">{product.name}</h1>

            {product.numReviews > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <FiStar key={i} size={16} className={i < Math.round(product.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
                  ))}
                </div>
                <span className="text-sm text-gray-600">({product.numReviews} reviews)</span>
              </div>
            )}

            <div className="flex items-end gap-3 mb-2">
              <span className="text-3xl font-extrabold text-primary-600">{formatPrice(product.price)}</span>
              {product.comparePrice > 0 && (
                <>
                  <span className="text-xl text-gray-400 line-through">{formatPrice(product.comparePrice)}</span>
                  {discount > 0 && (
                    <span className="text-sm font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded-lg">{discount}% OFF</span>
                  )}
                </>
              )}
            </div>

            {product.shortDescription && (
              <p className="text-gray-600 mb-4 leading-relaxed text-sm">{product.shortDescription}</p>
            )}

            <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-500 mb-3">
              {product.sku && <p>SKU: <span className="font-semibold text-gray-700">{product.sku}</span></p>}
              {product.brand && <p>Brand: <span className="font-semibold text-gray-700">{product.brand}</span></p>}
            </div>

            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-5 ${product.stock > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {product.stock > 0 ? `In Stock (${product.stock} available)` : 'Out of Stock'}
            </div>

            {product.stock > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-primary-500 transition-colors">
                    <FiMinus size={16} />
                  </button>
                  <span className="w-12 text-center font-semibold text-lg">{qty}</span>
                  <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="w-10 h-10 border-2 border-gray-300 rounded-lg flex items-center justify-center hover:border-primary-500 transition-colors">
                    <FiPlus size={16} />
                  </button>
                </div>
                <button onClick={handleAddToCart} className="w-full btn-primary flex items-center justify-center gap-2 py-3 text-base">
                  <FiShoppingCart size={18} /> Add to Cart
                </button>
                <Link to="/checkout" onClick={() => addToCart(product, qty)} className="w-full btn-secondary flex items-center justify-center py-3 text-base">
                  Buy Now
                </Link>
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                <FiTruck size={16} className="text-primary-500" />
                <span>Free delivery above PKR 5,000</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                <FiShield size={16} className="text-primary-500" />
                <span>Quality guaranteed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="card p-6 mb-8">
        <div className="border-b border-gray-200 mb-6">
          <h2 className="font-bold text-lg pb-3 border-b-2 border-primary-500 inline-block text-primary-600">Description</h2>
        </div>
        <div className="prose max-w-none text-gray-600 leading-relaxed">
          <p>{product.description}</p>
        </div>

        {product.specifications?.length > 0 && (
          <div className="mt-6">
            <h3 className="font-semibold text-gray-800 mb-3">Technical Specifications</h3>
            <div className="overflow-hidden rounded-xl border border-gray-100">
              {product.specifications.map((spec, i) => (
                <div key={i} className={`flex items-start gap-4 px-4 py-3 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                  <span className="text-sm text-gray-500 w-40 flex-shrink-0 font-medium">{spec.key}</span>
                  <span className="text-sm font-semibold text-gray-800">{spec.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Packing Info */}
        {product.packingInfo && (product.packingInfo.unitsPerCarton || product.packingInfo.cartonDimensions || product.packingInfo.cartonWeight) && (
          <div className="mt-6">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <FiPackage size={16} className="text-primary-500" /> Packing Information
            </h3>
            <div className="bg-primary-50 border border-primary-100 rounded-xl p-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {product.packingInfo.unitsPerCarton && (
                  <div className="bg-white rounded-lg p-3 text-center border border-primary-100">
                    <p className="text-xs text-gray-500 mb-1">Units / Carton</p>
                    <p className="font-bold text-gray-800 text-sm">{product.packingInfo.unitsPerCarton}</p>
                  </div>
                )}
                {product.packingInfo.cartonDimensions && (
                  <div className="bg-white rounded-lg p-3 text-center border border-primary-100">
                    <p className="text-xs text-gray-500 mb-1">Carton Size</p>
                    <p className="font-bold text-gray-800 text-sm">{product.packingInfo.cartonDimensions}</p>
                  </div>
                )}
                {product.packingInfo.cartonWeight && (
                  <div className="bg-white rounded-lg p-3 text-center border border-primary-100">
                    <p className="text-xs text-gray-500 mb-1">Carton Weight</p>
                    <p className="font-bold text-gray-800 text-sm">{product.packingInfo.cartonWeight}</p>
                  </div>
                )}
              </div>
              {product.packingInfo.note && (
                <p className="text-xs text-primary-600 mt-3 font-medium">{product.packingInfo.note}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Reviews */}
      <div className="card p-6 mb-8">
        <h2 className="font-bold text-lg mb-6">Reviews ({product.numReviews})</h2>
        {product.reviews?.map((r) => (
          <div key={r._id} className="border-b border-gray-100 pb-4 mb-4 last:border-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-sm">{r.name}</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <FiStar key={i} size={12} className={i < r.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'} />
                ))}
              </div>
            </div>
            <p className="text-gray-600 text-sm">{r.comment}</p>
          </div>
        ))}

        {user && (
          <form onSubmit={handleReviewSubmit} className="mt-6 border-t border-gray-100 pt-6">
            <h3 className="font-semibold mb-4">Write a Review</h3>
            <div className="mb-3">
              <label className="text-sm text-gray-600 mb-1 block">Rating</label>
              <select value={review.rating} onChange={(e) => setReview({ ...review, rating: Number(e.target.value) })} className="input-field w-auto">
                {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} Star{r > 1 ? 's' : ''}</option>)}
              </select>
            </div>
            <div className="mb-3">
              <label className="text-sm text-gray-600 mb-1 block">Comment</label>
              <textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} required rows={3} className="input-field" placeholder="Share your experience..." />
            </div>
            <button type="submit" disabled={submittingReview} className="btn-primary">
              {submittingReview ? 'Submitting...' : 'Submit Review'}
            </button>
          </form>
        )}
      </div>

      {/* Related products */}
      {related.length > 0 && (
        <div>
          <h2 className="section-title mb-6">Related Products</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {related.map((p) => <ProductCard key={p._id} product={p} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
